'use client';

/**
 * RFQQuoteAnalysis - AI-assisted analysis of vendor quotes for an RFQ
 *
 * Analyzes vendor responses to provide:
 * - Price spread analysis (best/worst per product)
 * - Lead time comparison
 * - Missing items identification
 * - Award recommendation
 *
 * @component_role RFQ-level AI Insights
 * @business_rule Analyzes vendor responses for a specific RFQ. AI compares quoted
 *   prices, lead times, and terms across vendors. Identifies outliers (too cheap =
 *   quality risk, too expensive = negotiation opportunity). Suggests optimal vendor
 *   mix for split orders.
 * @user_outcome Data-driven vendor selection - understand not just who's cheapest,
 *   but who offers the best value considering lead time, reliability, and terms.
 * @integration_point klyde_edge /neo/graph/compare/rfq
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain copilot
 */

import {
  Loader2,
  XCircle,
  AlertTriangle,
  TrendingDown,
  TrendingUp,
  Clock,
  CheckCircle,
  Package,
  DollarSign,
  BarChart3,
} from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import { useRFQComparison } from '@/lib/api/hooks';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { useMemo } from 'react';

interface RFQQuoteAnalysisProps {
  rfqId: string;
  projectId?: string | null;
}

export function RFQQuoteAnalysis({ rfqId, projectId }: RFQQuoteAnalysisProps) {
  const rfqComparisonStrategy = useCapabilityStore((s) => s.strategyName('rfi', 'comparison'));
  const { data, isLoading, error } = useRFQComparison(rfqComparisonStrategy!, { rfq_id: rfqId });

  // Compute analysis metrics
  const analysis = useMemo(() => {
    if (!data || data.vendors.length === 0) return null;

    const vendors = data.vendors;
    const products = data.products;
    const matrix = data.matrix;

    // Calculate per-product best prices
    const productAnalysis = products.map(product => {
      const prices: { vendorId: string; vendorName: string; price: number }[] = [];

      vendors.forEach(vendor => {
        const cell = matrix[product.semantic_id]?.[vendor.semantic_id];
        if (cell?.unit_price) {
          prices.push({
            vendorId: vendor.semantic_id,
            vendorName: vendor.name,
            price: cell.unit_price,
          });
        }
      });

      const sortedPrices = [...prices].sort((a, b) => a.price - b.price);
      const best = sortedPrices[0];
      const worst = sortedPrices[sortedPrices.length - 1];
      const spread = best && worst ? ((worst.price - best.price) / best.price * 100) : 0;

      return {
        product,
        quotedCount: prices.length,
        totalVendors: vendors.length,
        best,
        worst,
        spread,
        missingQuotes: vendors.length - prices.length,
      };
    });

    // Calculate vendor totals
    const vendorTotals = vendors.map(vendor => {
      let total = 0;
      let quotedItems = 0;
      let avgLeadTime = 0;
      let leadTimeCount = 0;

      products.forEach(product => {
        const cell = matrix[product.semantic_id]?.[vendor.semantic_id];
        if (cell?.unit_price) {
          total += cell.unit_price * (cell.quantity_offered || 1);
          quotedItems++;
        }
        if (cell?.lead_time_days) {
          avgLeadTime += cell.lead_time_days;
          leadTimeCount++;
        }
      });

      return {
        vendor,
        total,
        quotedItems,
        coverage: (quotedItems / products.length) * 100,
        avgLeadTime: leadTimeCount > 0 ? avgLeadTime / leadTimeCount : null,
      };
    }).sort((a, b) => a.total - b.total);

    // Find products with high price variance
    const highVarianceProducts = productAnalysis
      .filter(p => p.spread > 20)
      .sort((a, b) => b.spread - a.spread);

    // Find missing quotes
    const missingQuotes = productAnalysis.filter(p => p.missingQuotes > 0);

    // Best overall vendor (lowest total with full coverage)
    const fullCoverageVendors = vendorTotals.filter(v => v.coverage === 100);
    const bestVendor = fullCoverageVendors[0] || vendorTotals[0];

    return {
      productAnalysis,
      vendorTotals,
      highVarianceProducts,
      missingQuotes,
      bestVendor,
      totalProducts: products.length,
      totalVendors: vendors.length,
      respondedVendors: vendors.filter(v => v.response_status === 'quoted').length,
    };
  }, [data]);

  // Loading state
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center text-muted-foreground">
        <Loader2 className="h-8 w-8 mb-2 animate-spin" />
        <p className="text-sm">Analyzing quotes...</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center text-destructive">
        <XCircle className="h-8 w-8 mb-2" />
        <p className="text-sm">Failed to analyze quotes</p>
        <p className="text-xs mt-1">{(error as Error).message}</p>
      </div>
    );
  }

  // No data
  if (!analysis || !data) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center text-muted-foreground">
        <BarChart3 className="h-8 w-8 mb-2 opacity-50" />
        <p className="text-sm">No vendor responses yet</p>
        <p className="text-xs mt-1">Waiting for vendors to submit quotes</p>
      </div>
    );
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  return (
    <div className="space-y-4 p-3">
      {/* Header */}
      <div>
        <h4 className="text-xs font-semibold text-muted-foreground mb-2">QUOTE ANALYSIS</h4>
        <div className="p-3 rounded-lg bg-card border border-border">
          <div className="font-medium">{data.rfq_name || 'RFQ'}</div>
          <div className="flex items-center gap-2 mt-2 text-xs">
            <span className="px-1.5 py-0.5 rounded bg-muted">{analysis.totalVendors} vendors</span>
            <span className="px-1.5 py-0.5 rounded bg-muted">{analysis.totalProducts} products</span>
            <span className={cn(
              "px-1.5 py-0.5 rounded",
              data.metadata.rfq_status === 'sent' && "bg-info/20 text-info",
              data.metadata.rfq_status === 'done' && "bg-success/20 text-success"
            )}>
              {data.metadata.rfq_status}
            </span>
          </div>
        </div>
      </div>

      {/* Best Vendor Recommendation */}
      {analysis.bestVendor && (
        <div>
          <h4 className="text-xs font-semibold text-muted-foreground mb-2">RECOMMENDATION</h4>
          <div className="p-3 rounded-lg bg-success/10 border border-success/20">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-success" />
              <span className="font-medium text-sm">{analysis.bestVendor.vendor.name}</span>
            </div>
            <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
              <div>
                <span className="text-muted-foreground">Total:</span>
                <span className="ml-1 font-medium">{formatCurrency(analysis.bestVendor.total)}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Coverage:</span>
                <span className="ml-1 font-medium">{analysis.bestVendor.coverage.toFixed(0)}%</span>
              </div>
              {analysis.bestVendor.avgLeadTime && (
                <div className="col-span-2">
                  <span className="text-muted-foreground">Avg Lead Time:</span>
                  <span className="ml-1 font-medium">{analysis.bestVendor.avgLeadTime.toFixed(0)} days</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Vendor Comparison */}
      <div>
        <h4 className="text-xs font-semibold text-muted-foreground mb-2">VENDOR TOTALS</h4>
        <div className="space-y-2">
          {analysis.vendorTotals.slice(0, 4).map((vt, idx) => (
            <div
              key={vt.vendor.semantic_id}
              className={cn(
                "flex items-center justify-between p-2 rounded border",
                idx === 0 ? "bg-success/5 border-success/20" : "bg-card border-border"
              )}
            >
              <div className="flex items-center gap-2">
                {idx === 0 && <TrendingDown className="h-3 w-3 text-success" />}
                <span className="text-sm">{vt.vendor.name}</span>
              </div>
              <div className="text-right">
                <div className="text-sm font-medium">{formatCurrency(vt.total)}</div>
                <div className="text-xs text-muted-foreground">
                  {vt.quotedItems}/{analysis.totalProducts} items
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* High Variance Warning */}
      {analysis.highVarianceProducts.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-muted-foreground mb-2">PRICE VARIANCE</h4>
          <div className="space-y-2">
            {analysis.highVarianceProducts.slice(0, 3).map(p => (
              <div key={p.product.semantic_id} className="p-2 rounded bg-warning/10 border border-warning/20">
                <div className="flex items-start gap-2">
                  <TrendingUp className="h-4 w-4 text-warning shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{p.product.name}</div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {p.spread.toFixed(0)}% spread: {p.best?.vendorName} ({formatCurrency(p.best?.price || 0)})
                      vs {p.worst?.vendorName} ({formatCurrency(p.worst?.price || 0)})
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Missing Quotes */}
      {analysis.missingQuotes.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-muted-foreground mb-2">INCOMPLETE QUOTES</h4>
          <div className="p-2 rounded bg-muted/50 border border-border">
            <div className="flex items-center gap-2 text-sm">
              <Package className="h-4 w-4 text-muted-foreground" />
              <span>{analysis.missingQuotes.length} products have incomplete vendor coverage</span>
            </div>
            <div className="mt-2 text-xs text-muted-foreground">
              {analysis.missingQuotes.slice(0, 3).map(p => p.product.name).join(', ')}
              {analysis.missingQuotes.length > 3 && ` +${analysis.missingQuotes.length - 3} more`}
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="text-xs text-muted-foreground pt-2 border-t border-border">
        Analysis based on {analysis.totalVendors} vendor responses
      </div>
    </div>
  );
}
